import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

const VARIANT_HOVER_PREVIEW_CONFIG = Object.freeze({
  arrowInset: 22,
  gap: 14,
  viewportPadding: 16,
});

/**
 * @param {number} value
 * @param {number} min
 * @param {number} max
 */
const clampValue = (value, min, max) => Math.min(Math.max(value, min), max);

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  /** @type {HTMLDivElement | null} */
  #hoverPreview = null;

  /** @type {HTMLImageElement | null} */
  #hoverPreviewImage = null;

  /** @type {HTMLElement | null} */
  #activePreviewTrigger = null;

  #boundHandlePointerOver = this.handlePointerOver.bind(this);

  #boundHandlePointerMove = this.handlePointerMove.bind(this);

  #boundHandlePointerOut = this.handlePointerOut.bind(this);

  #boundHandleFocusIn = this.handleFocusIn.bind(this);

  #boundHandleFocusOut = this.handleFocusOut.bind(this);

  #boundHandleClick = this.handleClick.bind(this);

  #boundHandleDocumentPointerDown = this.handleDocumentPointerDown.bind(this);

  #boundHideHoverPreview = this.hideHoverPreview.bind(this);

  #boundHandleOptionCatalogClick = this.handleOptionCatalogClick.bind(this);

  connectedCallback() {
    super.connectedCallback();
    this.removeVariantParameterFromUrl();

    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.addEventListener('click', this.#boundHandleOptionCatalogClick);
    this.#resizeObserver.observe(this);
    this.initializeVariantSelects();

    this.querySelectorAll('[data-option-item-input]:checked').forEach((input) => {
      if (input instanceof HTMLInputElement) this.collapseOptionCatalog(input, false);
    });

    if (this.canShowHoverPreview) {
      this.addEventListener('pointerover', this.#boundHandlePointerOver);
      this.addEventListener('pointermove', this.#boundHandlePointerMove);
      this.addEventListener('pointerout', this.#boundHandlePointerOut);
      this.addEventListener('focusin', this.#boundHandleFocusIn);
      this.addEventListener('focusout', this.#boundHandleFocusOut);
      this.addEventListener('click', this.#boundHandleClick);
      document.addEventListener('pointerdown', this.#boundHandleDocumentPointerDown, true);
      window.addEventListener('scroll', this.#boundHideHoverPreview, { passive: true });
      this.ensureHoverPreview();
    }
  }

  /**
   * Keeps product URLs clean while preserving the server-selected variant state.
   */
  removeVariantParameterFromUrl() {
    if (this.dataset.templateProductMatch !== 'true') return;

    const url = new URL(window.location.href);
    if (!url.searchParams.has('variant')) return;

    url.searchParams.delete('variant');
    history.replaceState(history.state, '', url.toString());
  }

  initializeVariantSelects() {
    this.querySelectorAll('[data-variant-select]').forEach((select) => {
      if (!(select instanceof HTMLSelectElement)) return;

      const selectableOptions = Array.from(select.options).filter(
        (option) => !option.hasAttribute('data-variant-placeholder') && !option.disabled
      );
      const autoSelectedOption = select.hasAttribute('data-auto-select-single') && selectableOptions.length === 1
        ? selectableOptions[0]
        : undefined;

      this.syncVariantSelect(select, Boolean(autoSelectedOption), autoSelectedOption?.value || '');
    });
  }

  /**
   * Keeps a variant placeholder and its optional catalog in sync.
   * @param {HTMLSelectElement} select
   * @param {boolean} hasSelection
   * @param {string} [selectedValue]
   */
  syncVariantSelect(select, hasSelection, selectedValue = '') {
    const placeholder = select.querySelector('[data-variant-placeholder]');
    const optionPosition = select.dataset.optionPosition;
    const catalog = optionPosition
      ? this.querySelector(`[data-option-catalog][data-option-position="${CSS.escape(optionPosition)}"]`)
      : null;

    if (!hasSelection) {
      for (const option of select.options) {
        option.toggleAttribute('selected', option === placeholder);
      }
      select.value = '';
      delete select.dataset.userSelected;
    } else {
      const catalogValue = catalog instanceof HTMLElement ? catalog.dataset.selectedGroup || '' : '';
      const resolvedValue = selectedValue || catalogValue || select.value;
      const selectedOption = Array.from(select.options).find((option) => option.value === resolvedValue);

      if (selectedOption) {
        for (const option of select.options) {
          option.toggleAttribute('selected', option === selectedOption);
        }
        select.value = selectedOption.value;
        select.dataset.userSelected = 'true';
      }
    }

    if (!(catalog instanceof HTMLElement)) return;

    const catalogMatchesSelection = hasSelection && (!selectedValue || catalog.dataset.selectedGroup === selectedValue);
    catalog.hidden = !catalogMatchesSelection;
    catalog.querySelectorAll('[data-option-item-input]').forEach((input) => {
      if (input instanceof HTMLInputElement) input.disabled = !catalogMatchesSelection;
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.removeEventListener('click', this.#boundHandleOptionCatalogClick);

    if (this.canShowHoverPreview) {
      this.removeEventListener('pointerover', this.#boundHandlePointerOver);
      this.removeEventListener('pointermove', this.#boundHandlePointerMove);
      this.removeEventListener('pointerout', this.#boundHandlePointerOut);
      this.removeEventListener('focusin', this.#boundHandleFocusIn);
      this.removeEventListener('focusout', this.#boundHandleFocusOut);
      this.removeEventListener('click', this.#boundHandleClick);
      document.removeEventListener('pointerdown', this.#boundHandleDocumentPointerDown, true);
      window.removeEventListener('scroll', this.#boundHideHoverPreview);
      this.hideHoverPreview();
      this.#hoverPreview?.remove();
      this.#hoverPreview = null;
      this.#hoverPreviewImage = null;
    }
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event.target instanceof HTMLInputElement && event.target.matches('[data-option-item-input]')) {
      this.collapseOptionCatalog(event.target);
      return;
    }

    if (event.target instanceof HTMLSelectElement && event.target.matches('[data-variant-select]')) {
      this.syncVariantSelect(event.target, true, event.target.value);
    }

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;
    if (!selectedOption.dataset.optionValueId) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({
      id: selectedOption.dataset.optionValueId ?? '',
    }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector);

    const url = new URL(window.location.href);

    if (isOnProductPage) {
      url.searchParams.delete('variant');
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        // Dispatch for all paths so product-form-component can reset #variantChangeInProgress
        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
            })
          );
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;
    const optionCatalogSelections = this.#getOptionCatalogSelections();
    const variantSelectSelections = this.#getVariantSelectSelections();

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.#restoreVariantSelectSelections(variantSelectSelections);
    this.#restoreOptionCatalogSelections(optionCatalogSelections);
    this.updateVariantPickerCss();
    return newProduct;
  }

  /** @returns {Map<string, string>} */
  #getVariantSelectSelections() {
    const selections = new Map();

    this.querySelectorAll('[data-variant-select][data-user-selected="true"]').forEach((select) => {
      if (select instanceof HTMLSelectElement && select.dataset.optionPosition && select.value) {
        selections.set(select.dataset.optionPosition, select.value);
      }
    });

    return selections;
  }

  /** @param {Map<string, string>} selections */
  #restoreVariantSelectSelections(selections) {
    this.querySelectorAll('[data-variant-select]').forEach((select) => {
      if (!(select instanceof HTMLSelectElement)) return;

      const selectedValue = select.dataset.optionPosition
        ? selections.get(select.dataset.optionPosition)
        : undefined;
      this.syncVariantSelect(select, Boolean(selectedValue), selectedValue || '');
    });
  }

  /** @returns {Map<string, string>} */
  #getOptionCatalogSelections() {
    const selections = new Map();

    this.querySelectorAll('[data-option-item-input]:checked').forEach((input) => {
      if (input instanceof HTMLInputElement && input.name) {
        selections.set(input.name, input.value);
      }
    });

    return selections;
  }

  /** @param {Map<string, string>} selections */
  #restoreOptionCatalogSelections(selections) {
    if (!selections.size) return;

    for (const [name, value] of selections) {
      const inputs = Array.from(this.querySelectorAll('[data-option-item-input]')).filter(
        (input) => input instanceof HTMLInputElement && input.name === name
      );
      const matchingInput = inputs.find((input) => input instanceof HTMLInputElement && input.value === value);

      if (!(matchingInput instanceof HTMLInputElement)) continue;

      inputs.forEach((input) => {
        if (!(input instanceof HTMLInputElement)) return;

        const isSelected = input === matchingInput;
        input.checked = isSelected;
        input.toggleAttribute('checked', isSelected);
        input.removeAttribute('aria-invalid');
      });

      const fieldset = matchingInput.closest('[data-option-catalog]');
      if (fieldset instanceof HTMLElement) {
        fieldset.removeAttribute('data-validation-state');
        fieldset.removeAttribute('aria-invalid');
        fieldset.removeAttribute('aria-describedby');

        const error = fieldset.querySelector('[data-option-catalog-error]');
        if (error instanceof HTMLElement) {
          error.hidden = true;
          error.textContent = '';
        }
      }

      this.collapseOptionCatalog(matchingInput, false);
    }
  }

  /**
   * @param {HTMLInputElement} input
   * @param {boolean} [moveFocus]
   */
  collapseOptionCatalog(input, moveFocus = true) {
    const fieldset = input.closest('[data-option-catalog]');
    if (!(fieldset instanceof HTMLElement)) return;

    const option = input.closest('.option-catalog__option');
    const list = fieldset.querySelector('[data-option-catalog-list]');
    const selection = fieldset.querySelector('[data-option-catalog-selection]');
    const selectionName = fieldset.querySelector('[data-option-catalog-selection-name]');
    const selectionMedia = fieldset.querySelector('[data-option-catalog-selection-media]');
    const selectionImage = fieldset.querySelector('[data-option-catalog-selection-image]');
    const reset = fieldset.querySelector('[data-option-catalog-reset]');

    if (!(option instanceof HTMLElement) || !(selection instanceof HTMLElement)) return;

    const name = option.querySelector('.option-catalog__name')?.textContent?.trim() || input.value;
    const sourceImage = option.querySelector('.option-catalog__swatch img');
    const previewImage = option.dataset.previewImage || (sourceImage instanceof HTMLImageElement ? sourceImage.src : '');
    const previewAlt = option.dataset.previewAlt || name;

    if (selectionName instanceof HTMLElement) selectionName.textContent = name;

    if (selectionMedia instanceof HTMLElement && selectionImage instanceof HTMLImageElement) {
      if (sourceImage instanceof HTMLImageElement && (sourceImage.currentSrc || sourceImage.src)) {
        selectionImage.src = sourceImage.currentSrc || sourceImage.src;
        selectionImage.alt = '';
        selectionImage.hidden = false;
        selectionMedia.hidden = false;
        selectionMedia.dataset.previewImage = previewImage;
        selectionMedia.dataset.previewAlt = previewAlt;
        selectionMedia.setAttribute('aria-label', `Pré-visualizar ${name}`);
      } else {
        selectionImage.removeAttribute('src');
        selectionImage.hidden = true;
        selectionMedia.hidden = true;
        delete selectionMedia.dataset.previewImage;
        delete selectionMedia.dataset.previewAlt;
      }
    }

    if (list instanceof HTMLElement) list.hidden = true;
    selection.hidden = false;
    fieldset.dataset.collapsed = 'true';
    this.hideHoverPreview();

    if (moveFocus && reset instanceof HTMLButtonElement) {
      queueMicrotask(() => reset.focus({ preventScroll: true }));
    }
  }

  /** @param {HTMLElement} fieldset */
  expandOptionCatalog(fieldset) {
    const list = fieldset.querySelector('[data-option-catalog-list]');
    const selection = fieldset.querySelector('[data-option-catalog-selection]');
    const selectionMedia = fieldset.querySelector('[data-option-catalog-selection-media]');

    fieldset.querySelectorAll('[data-option-item-input]').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      input.checked = false;
      input.removeAttribute('checked');
    });

    if (list instanceof HTMLElement) list.hidden = false;
    if (selection instanceof HTMLElement) selection.hidden = true;
    if (selectionMedia instanceof HTMLElement) {
      delete selectionMedia.dataset.previewImage;
      delete selectionMedia.dataset.previewAlt;
    }
    delete fieldset.dataset.collapsed;

    const firstInput = fieldset.querySelector('[data-option-item-input]');
    if (firstInput instanceof HTMLInputElement) firstInput.focus({ preventScroll: true });
  }

  /** @param {MouseEvent} event */
  handleOptionCatalogClick(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const reset = target.closest('[data-option-catalog-reset]');
    if (!(reset instanceof HTMLElement)) return;

    const fieldset = reset.closest('[data-option-catalog]');
    if (!(fieldset instanceof HTMLElement)) return;

    event.preventDefault();
    this.expandOptionCatalog(fieldset);
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  get canShowHoverPreview() {
    return this.tagName.toLowerCase() === 'variant-picker' && this.dataset.templateProductMatch === 'true';
  }

  get canShowTouchPreview() {
    return (
      typeof window.matchMedia === 'function' &&
      (window.matchMedia('(hover: none)').matches || window.matchMedia('(pointer: coarse)').matches)
    );
  }

  ensureHoverPreview() {
    if (this.#hoverPreview) return;

    const preview = document.createElement('div');
    preview.className = 'variant-picker-hover-preview';
    preview.setAttribute('aria-hidden', 'true');

    const image = document.createElement('img');
    image.alt = '';
    image.loading = 'lazy';

    preview.append(image);
    document.body.append(preview);

    this.#hoverPreview = preview;
    this.#hoverPreviewImage = image;
  }

  /**
   * @param {HTMLElement | null} target
   * @returns {HTMLElement | null}
   */
  getPreviewTrigger(target) {
    if (!target) return null;

    const trigger = target.closest('[data-preview-image]');
    if (trigger instanceof HTMLElement) return trigger;

    const swatchTrigger = target.closest('.variant-option__button-label--has-swatch');
    return swatchTrigger instanceof HTMLElement ? swatchTrigger : null;
  }

  /**
   * @param {string | null | undefined} value
   * @returns {string | null}
   */
  extractImageUrl(value) {
    if (!value) return null;

    const trimmedValue = value.trim();
    const match = trimmedValue.match(/url\((['"]?)(.*?)\1\)/);
    return match?.[2] || null;
  }

  /**
   * @param {HTMLElement} trigger
   * @returns {string | null}
   */
  getPreviewImageSource(trigger) {
    if (trigger.dataset.previewImage) {
      return trigger.dataset.previewImage;
    }

    const swatch = trigger.querySelector('.swatch');
    if (!(swatch instanceof HTMLElement)) return null;

    const computedStyle = getComputedStyle(swatch);
    return (
      this.extractImageUrl(computedStyle.backgroundImage) ||
      this.extractImageUrl(computedStyle.getPropertyValue('--swatch-background'))
    );
  }

  /**
   * @param {HTMLElement} trigger
   */
  showHoverPreview(trigger) {
    const previewImage = this.getPreviewImageSource(trigger);
    if (!previewImage) {
      this.hideHoverPreview();
      return;
    }

    this.ensureHoverPreview();
    if (!this.#hoverPreview || !this.#hoverPreviewImage) return;

    this.#activePreviewTrigger = trigger;
    this.#hoverPreviewImage.alt = trigger.dataset.previewAlt || '';
    this.#hoverPreview.classList.remove('is-visible');
    this.#hoverPreviewImage.removeAttribute('src');
    this.positionHoverPreview(trigger);

    const showLoadedPreview = () => {
      if (this.#activePreviewTrigger !== trigger) return;

      this.#hoverPreview?.classList.add('is-visible');
      this.positionHoverPreview(trigger);
    };

    const hideFailedPreview = () => {
      if (this.#activePreviewTrigger === trigger) {
        this.hideHoverPreview();
      }
    };

    this.#hoverPreviewImage.addEventListener('load', showLoadedPreview, { once: true });
    this.#hoverPreviewImage.addEventListener('error', hideFailedPreview, { once: true });
    this.#hoverPreviewImage.src = previewImage;

    if (this.#hoverPreviewImage.complete && this.#hoverPreviewImage.naturalWidth > 0) {
      showLoadedPreview();
    }
  }

  hideHoverPreview() {
    this.#activePreviewTrigger = null;
    this.#hoverPreview?.classList.remove('is-visible');
  }

  /**
   * @param {HTMLElement} trigger
   */
  positionHoverPreview(trigger) {
    if (!this.#hoverPreview) return;

    const rect = trigger.getBoundingClientRect();
    const previewRect = this.#hoverPreview.getBoundingClientRect();
    const { arrowInset, gap, viewportPadding } = VARIANT_HOVER_PREVIEW_CONFIG;
    const triggerCenter = rect.left + rect.width / 2;
    const spaceAbove = rect.top - viewportPadding;
    const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
    const fitsAbove = spaceAbove >= previewRect.height + gap;
    const fitsBelow = spaceBelow >= previewRect.height + gap;

    let left = rect.left + rect.width / 2 - previewRect.width / 2;
    const maxLeft = Math.max(viewportPadding, window.innerWidth - previewRect.width - viewportPadding);
    let top;
    let previewPosition;

    left = clampValue(left, viewportPadding, maxLeft);

    if (fitsAbove || (!fitsBelow && spaceAbove >= spaceBelow)) {
      top = Math.max(viewportPadding, rect.top - previewRect.height - gap);
      previewPosition = 'above';
    } else {
      top = Math.min(rect.bottom + gap, window.innerHeight - previewRect.height - viewportPadding);
      previewPosition = 'below';
    }

    const arrowOffset = clampValue(triggerCenter - left, arrowInset, previewRect.width - arrowInset);

    this.#hoverPreview.style.left = `${left}px`;
    this.#hoverPreview.style.top = `${top}px`;
    this.#hoverPreview.style.setProperty('--preview-arrow-offset', `${arrowOffset}px`);
    this.#hoverPreview.dataset.previewPosition = previewPosition;
  }

  /**
   * @param {PointerEvent} event
   */
  handlePointerOver(event) {
    if (event.pointerType === 'touch') return;

    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const trigger = this.getPreviewTrigger(target);
    if (!trigger) return;

    this.showHoverPreview(trigger);
  }

  /**
   * @param {PointerEvent} event
   */
  handlePointerMove(event) {
    if (!this.#activePreviewTrigger) return;
    this.positionHoverPreview(this.#activePreviewTrigger);
  }

  /**
   * @param {PointerEvent} event
   */
  handlePointerOut(event) {
    if (event.pointerType === 'touch') return;

    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const trigger = this.getPreviewTrigger(target);
    if (!trigger) return;

    const relatedTarget = event.relatedTarget;
    if (relatedTarget instanceof Node && trigger.contains(relatedTarget)) return;

    this.hideHoverPreview();
  }

  /**
   * Shows the same variant preview on tap that pointer devices show on hover.
   * The label's native click remains untouched, so the variant is still selected.
   * @param {MouseEvent} event
   */
  handleClick(event) {
    if (!this.canShowTouchPreview) return;

    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const trigger = this.getPreviewTrigger(target);
    if (!trigger) return;

    this.showHoverPreview(trigger);
  }

  /**
   * Closes a touch preview when the customer taps anywhere outside a preview trigger.
   * @param {PointerEvent} event
   */
  handleDocumentPointerDown(event) {
    if (!this.#activePreviewTrigger || !this.canShowTouchPreview) return;

    const target = event.target;
    if (target instanceof HTMLElement && this.getPreviewTrigger(target)) return;

    this.hideHoverPreview();
  }

  /**
   * @param {FocusEvent} event
   */
  handleFocusIn(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (target.matches('[data-option-item-input]')) return;

    const trigger = this.getPreviewTrigger(target);
    if (!trigger) return;

    this.showHoverPreview(trigger);
  }

  /**
   * @param {FocusEvent} event
   */
  handleFocusOut(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const trigger = this.getPreviewTrigger(target);
    if (!trigger) return;

    const relatedTarget = event.relatedTarget;
    if (relatedTarget instanceof Node && trigger.contains(relatedTarget)) return;

    this.hideHoverPreview();
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector(
      'select option[data-option-value-id][selected], fieldset input[data-option-value-id]:checked'
    );

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(
      this.querySelectorAll('select option[data-option-value-id][selected], fieldset input[data-option-value-id]:checked')
    );

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
